// src/features/locations/LeaseAndCostPane.jsx
//
// O-36: Overview "Lease & cost" pane — replaces the old "What's ahead"
// (DecisionsActionsCard) slot in the 2×2 Overview grid.
//
// Layout (top to bottom):
//   1. Header: "Lease & cost" title + lease-count tag
//   2. Compact lease headers: type, status, linked ID, rent, structure and expiry.
//   3. 3 neutral KPI tiles: Occupancy / yr · Cost / sf · Cost / seat
//      Aggregated across all active leases using computeOpexMetrics when
//      opex data is available; lease-only fallback otherwise.
//   4. Collapsed office Areas, reusing the canonical lifecycle detail renderer.
//   5. "What's ahead": key dates from all active leases (nearest
//      per type for multi-lease), plus project decisions — rendered as
//      labelled rows. Items more than 31 days overdue are excluded.
//
// Task #8581 (v4 Overview): order is now lease header row(s) → KPI tiles →
// collapsible Areas. "What's ahead" is rendered as a standalone card via
// `aheadPortalTarget` (a DOM node owned by LocationDetail's row 3) so the
// established fetch + derivation path stays single-sourced in this component.
// Without the prop the section renders inline as before.
//
// Self-fetches listLocationLeases and getLocationOpex; prop-driven for
// activeLease (primary), projectDecisions, and location.
//
// Financial visibility rule (CLAUDE.md): never log dollar amounts.

import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { T } from "../../app/tokens.js";
import { buildLeaseUrl } from "../../services/buildUrl.js";
import { listLocationLeases, listCriticalDates } from "../../data/leasesClient.js";
import { UpcomingEventRow } from "../../components/UpcomingEventRow.jsx";
import { fmtDate } from "../../utils/format.js";
import { selectRecordDateEvents } from "../leases/recordDateEvents.js";
import { getLocationOpex } from "../../data/locationsClient.js";
import { computeOpexMetrics, fmtDollarsHero } from "../../services/opexMetrics.js";
import { deriveLocationSqft } from "../../utils/locationSqftDerived.js";
import { currentLeaseFootprint } from "../../../shared/leaseAreaBasis.js";
import { annualAreaBasis } from "../../shared/leaseAnnualHeadlines.js";
import { LeaseStatusBadge } from "../../components/LeaseStatusBadge.jsx";
import { LEASE_STATUS_META, LEASE_STATUS_UNKNOWN } from "../leases/leaseStatusLabels.js";
import { statusFor } from "../leases/pages/components/LeasePortfolioFilters.jsx";
import { OFFICE_LEASE_LINK_TYPE_STYLE } from "./officeLeaseLinkStyle.js";
import { LeaseAreasStrip } from "./LeaseAreasStrip.jsx";
import { formatLongExpiryDate, isActionablePastDueExpiry, isPastExpiryDate } from "../leases/expiryDisplay.js";
import {
  deriveKeyDateItems,
  deriveDecisionItems,
  isWithinOverdueWindow,
  parseDateStr,
  normalizeProjectDecision,
} from "./DecisionsActionsCard.jsx";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isActiveLease(l) {
  const rawStatus = String(l.status ?? l.stage ?? "").toLowerCase();
  const bucket = statusFor(l);

  // Sub-Out is persisted as status="sublease_out" by the canonical Lease
  // editor, not status="active". Use the canonical bucket for terminal
  // exclusions, while retaining the legacy active-row contract.
  if (bucket === "draft" || bucket === "expired") return false;
  return rawStatus === "active" || rawStatus === "sublease_out";
}

// Format a date as "Mon YYYY" (e.g. "May 2037").
function fmtMonthYear(dateStr) {
  if (!dateStr) return null;
  const d = parseDateStr(String(dateStr));
  if (!d) return null;
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

// Map a key-date label to a short uppercase tag shown in the "What's ahead" rows.
function shortDateTag(label) {
  if (!label) return "Key Date";
  const l = label.toLowerCase();
  if (l.includes("notice"))       return "Notice";
  if (l.includes("expir"))        return "Expiry";
  if (l.includes("commencement")) return "Start";
  if (l.includes("break"))        return "Break";
  if (l.includes("escalation"))   return "Rent";
  if (l.includes("review"))       return "Review";
  return "Key Date";
}

// Collect all key-date items from a list of leases, dedup, and sort.
// Dedup: collapse rows sharing the same (label, date) pair — first occurrence wins.
// Sort: primary ascending by date, secondary ascending by label (alpha tie-breaker)
// so equal-date rows always appear in the same order regardless of input order.
export function deriveAllKeyDateItems(leases) {
  const all = leases.flatMap(l => {
    const selected = selectRecordDateEvents(l);
    return (selected.displayEvents ?? selected.events).filter(e => !e.completedAt);
  });

  // Two-key sort: date ascending, then label ascending
  all.sort((a, b) => {
    const da = parseDateStr(a.date);
    const db = parseDateStr(b.date);
    if (!da && !db) return (a.label ?? "").localeCompare(b.label ?? "");
    if (!da) return 1;
    if (!db) return -1;
    const timeDiff = da.getTime() - db.getTime();
    if (timeDiff !== 0) return timeDiff;
    return (a.label ?? "").localeCompare(b.label ?? "");
  });

  // Selector already reconciles proven equivalent term events. Preserve
  // distinct source obligations that happen to share a type and date.
  const seen = new Set();
  return all.filter((item) => {
    const key = `${item.leaseId}|${item.id}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

// ─── Sub-components ───────────────────────────────────────────────────────────

// KPI tile — neutral background, small uppercase label, large value.
function KpiTile({ label, value, testId }) {
  return (
    <div
      data-testid={testId}
      style={{
        background:   T.bg,
        border:       `1px solid ${T.border}`,
        borderRadius: 9,
        padding:      "9px 10px",
      }}
    >
      <div
        style={{
          fontSize:      T.fsXxs,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          color:         T.mid,
          fontWeight:    T.fwBold,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize:   T.fsLg,
          fontWeight: T.fwDisplay,
          marginTop:  2,
          color:      T.dark,
        }}
      >
        {value}
      </div>
    </div>
  );
}

// Single lease row.
function LeaseRow({ lease, locationStage, onOpenLease, onEditAreas }) {
  const navigate  = useNavigate();
  const leaseRef  = lease.id;
  const contractualRent = lease.contractualAnnualRentUsd !== undefined ? lease.contractualAnnualRentUsd : lease.annualRentUsd;
  const annualRent = contractualRent != null
    ? fmtDollarsHero(Number(contractualRent)) + "/yr"
    : null;
  const rawEnd   = lease.endDate ?? lease.end_date ?? null;
  const sentinel = !rawEnd || String(rawEnd).startsWith("9999");
  const expiryDate = rawEnd && !sentinel
    ? (isPastExpiryDate(rawEnd) ? formatLongExpiryDate(rawEnd) : fmtMonthYear(rawEnd))
    : null;
  const structure = typeof lease.opexStructure === "string" && lease.opexStructure.trim()
    ? lease.opexStructure.trim()
    : null;
  const leaseLink = leaseRef ? (
    <a
      data-testid="lac-lease-id-link"
      href={buildLeaseUrl(leaseRef)}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        if (onOpenLease) onOpenLease(leaseRef);
        else navigate(`/leases/${encodeURIComponent(leaseRef)}`);
      }}
      style={{ color: T.blue, fontWeight: T.fwSemibold, textDecoration: "none" }}
    >
      {leaseRef}
    </a>
  ) : null;
  const subParts = [
    leaseLink,
    annualRent && <span data-testid="lac-lease-rent">{annualRent}</span>,
    structure && <span data-testid="lac-lease-structure">{structure}</span>,
  ].filter(Boolean);

  const rawType  = lease.type ?? lease.leaseType ?? lease.lease_type ?? "Lease";
  const leaseType = String(rawType)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

  // The explicit persisted bucket wins; statusFor supplies the established
  // legacy fallback for rows imported before lease_status was introduced.
  // Pass the current office stage so active leases at vacated offices render
  // Vacant-Held rather than the generic In-Use fallback.
  const statusBucket = statusFor({
    ...lease,
    locationStage: lease.locationStage ?? locationStage,
  });
  const statusMeta = LEASE_STATUS_META[statusBucket] ?? LEASE_STATUS_UNKNOWN;

  return (
    <div
      data-testid="lac-lease-row"
      style={{
        display:     "flex",
        alignItems:  "center",
        flexWrap:    "wrap",
        gap:         "6px 11px",
        padding:     "9px 11px",
        border:      `1px solid ${T.border}`,
        borderRadius: 10,
        marginBottom: 8,
        background:  T.card,
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width:       26,
          height:      26,
          borderRadius: 7,
          background:  T.blueSoft,
          color:       T.blue,
          display:     "grid",
          placeItems:  "center",
          flexShrink:  0,
          fontSize:    T.fsBase,
          userSelect:  "none",
        }}
      >
        ▤
      </div>

      <div style={{ flex: "1 1 180px", minWidth: 0 }}>
        <div style={{ fontWeight: T.fwBold, fontSize: T.fsSm5 }}>
          {leaseType}
          <span
            data-testid="lac-lease-status"
            style={{
              display:      "inline-flex",
              alignItems:   "center",
              marginLeft:   7,
              verticalAlign: "middle",
              fontSize:     T.fsXxxs5,
              fontWeight:   T.fwBold,
              color:        statusMeta.color,
              whiteSpace:   "nowrap",
            }}
          >
            <LeaseStatusBadge status={statusBucket} canWrite={false} />
          </span>
        </div>
        <div
          data-testid="lac-lease-meta"
          style={{
            fontSize:     T.fsXs,
            color:        T.light,
            marginTop:    1,
            overflowWrap: "anywhere",
          }}
        >
          {subParts.map((part, index) => (
            <React.Fragment key={index}>
              {index > 0 ? " · " : ""}
              {part}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div style={{ marginLeft: "auto", textAlign: "right", flexShrink: 0 }}>
        {expiryDate && (
          <div data-testid="lac-lease-expiry" style={{ fontSize: T.fsXs, fontWeight: T.fwBold, color: T.dark, whiteSpace: "nowrap" }}>
            Expires {expiryDate}
          </div>
        )}
        {/* Open lease — navigate to /leases/:id (the Lease Object detail page). */}
        <button
          type="button"
          data-testid="lac-open-lease-link"
          onClick={() => onOpenLease ? onOpenLease(lease.id) : navigate(`/leases/${encodeURIComponent(lease.id)}`)}
          style={{
            ...OFFICE_LEASE_LINK_TYPE_STYLE,
            whiteSpace:     "nowrap",
            textDecoration: "none",
            background:     "none",
            border:         "none",
            cursor:         "pointer",
            padding:        0,
          }}
        >
          Open lease →
        </button>
        {onEditAreas && <button type="button" data-testid="lac-edit-areas"
          onClick={() => onEditAreas(lease.id)}
          style={{ ...OFFICE_LEASE_LINK_TYPE_STYLE, display: "block", marginLeft: "auto", background: "none", border: 0, cursor: "pointer" }}>
          Edit areas
        </button>}
      </div>
    </div>
  );
}

// Single "What's ahead" row — tag | label | date-right.
function AheadRow({ tag, label, dateStr, subline, event, isActionablePastDue = false, isFirst }) {
  const formatted = dateStr
    ? (event?.provenance === "ambiguous" ? fmtDate(dateStr) : isActionablePastDue ? formatLongExpiryDate(dateStr) : fmtMonthYear(dateStr) ?? "-")
    : null;

  return <UpcomingEventRow tag={tag} label={label} subline={subline} date={formatted} event={event}
    first={isFirst} urgent={isActionablePastDue} />;
}

// ─── LeaseAndCostPane ─────────────────────────────────────────────────────────

export function LeaseAndCostPane({ location, activeLease, projectDecisions, aheadPortalTarget = undefined, onOpenLease, onEditAreas }) {
  // allLeasesForId tracks which locationId the current allLeases was fetched for.
  // This guards against stale allLeases appearing on the render that fires
  // *before* the useEffect cleanup/reset runs when locationId changes.
  const [allLeases,    setAllLeases]    = useState([]);
  const [allLeasesForId, setAllLeasesForId] = useState(null);
  const [opex,         setOpex]         = useState(null);
  const [opexForId,    setOpexForId]    = useState(null);
  const [showAllDates, setShowAllDates] = useState(false);
  const [dateReadErrors, setDateReadErrors] = useState([]);

  const locationId = location?.id ?? null;

  // Self-fetch all leases and opex data when locationId changes (including → null).
  // Reset stale data immediately so old rows are not visible during the
  // in-flight fetch for the new location.
  useEffect(() => {
    let cancelled = false;

    // Always reset on any locationId change (including to null/undefined).
    setAllLeases([]);
    setAllLeasesForId(null);
    setOpex(null);
    setOpexForId(null);
    setShowAllDates(false);
    setDateReadErrors([]);

    if (!locationId) return;

    listLocationLeases(locationId)
      .then(async (data) => {
        const rows = Array.isArray(data) ? data : [];
        const errors = [];
        const enriched = await Promise.all(rows.map(async lease => {
          try {
            const dates = Array.isArray(lease.criticalDates) ? lease.criticalDates : await listCriticalDates(locationId, lease.id);
            if (!Array.isArray(dates)) throw new Error("Invalid critical date list");
            return { ...lease, criticalDates: dates };
          } catch {
            errors.push(lease.id);
            return { ...lease, criticalDates: [], dateReadFailed: true };
          }
        }));
        if (!cancelled) {
          setAllLeases(enriched);
          setDateReadErrors(errors);
          setAllLeasesForId(locationId);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAllLeases([]);
          setDateReadErrors(["lease list"]);
          setAllLeasesForId(locationId);
        }
      });

    getLocationOpex(locationId)
      .then((data) => {
        if (!cancelled) {
          setOpex(data);
          setOpexForId(locationId);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setOpex(null);
          setOpexForId(locationId);
        }
      });

    return () => { cancelled = true; };
  }, [locationId]);

  // ── Active leases ─────────────────────────────────────────────────────────
  // Only use allLeases when it was fetched for the current location — this
  // prevents the prior location's leases from appearing on the render that
  // fires between the prop change and the useEffect reset.
  // Only include activeLease prop when it belongs to the current location
  // (guard against the parent passing a stale activeLease for one render).
  const safeFetchedLeases = allLeasesForId === locationId ? allLeases : [];
  const safeOpex          = opexForId      === locationId ? opex      : null;
  const officeLeasesById = new Map();
  for (const lease of Array.isArray(location?.leases) ? location.leases : []) {
    if (lease?.id) officeLeasesById.set(lease.id, lease);
  }
  for (const lease of safeFetchedLeases) {
    if (!lease?.id) continue;
    const embedded = officeLeasesById.get(lease.id);
    officeLeasesById.set(lease.id, {
      ...embedded,
      ...lease,
      areas: Array.isArray(lease.areas) ? lease.areas : embedded?.areas,
    });
  }
  const officeAreaLeases = [...officeLeasesById.values()];
  const officeAreas = officeAreaLeases.flatMap((lease) => {
    const leaseAreas = Array.isArray(lease.areas) ? lease.areas : [];
    if (leaseAreas.length) {
      return leaseAreas.map((area) => ({ ...area, ownerLeaseId: lease.id }));
    }
    // A lease without discrete areas still represents occupied space. Include
    // it in expanded multi-lease detail, but let the single-lease strip use
    // the office/premises descriptor without adding a redundant detail row.
    if (officeAreaLeases.length > 1) {
      return [{
        id: `whole-building-${lease.id}`,
        name: lease.premises || location?.suite || "Entire Building",
        sizeSf: currentLeaseFootprint(lease).grossSf,
        ownerLeaseId: lease.id,
        isLegacyArea: true,
      }];
    }
    return [];
  });
  const officeTotalSize = deriveLocationSqft(officeAreaLeases);
  const officeAreaSummaryLease = { size: officeTotalSize };
  const fetchedActive     = safeFetchedLeases.filter(isActiveLease);
  const fetchedIds        = new Set(fetchedActive.map((l) => l.id));
  // Accept activeLease only when it belongs to this location or location is unknown.
  const activeLeaseFitsHere = !activeLease || !isActiveLease(activeLease) ? false
    : !locationId ? false
    : (activeLease.locationId ?? activeLease.location_id ?? locationId) === locationId;
  const activeLeases = activeLeaseFitsHere && !fetchedIds.has(activeLease.id)
    ? [activeLease, ...fetchedActive]
    : fetchedActive;
  const leaseCount   = activeLeases.length;
  const rateYear = new Date().getFullYear();
  const annualAreas = activeLeases.map((lease) => annualAreaBasis(lease, rateYear, { months: 12 }));
  const annualSf = annualAreas.length && annualAreas.every((area) => area != null)
    && activeLeases.every((lease) => lease.type !== "sublease_outbound"
      && (lease.contractualAnnualRentUsd ?? lease.annualRentUsd) != null)
    ? annualAreas.reduce((sum, area) => sum + area, 0) : null;

  // ── Cost KPI computation ──────────────────────────────────────────────────
  const seatCount  = location?.installedCapacity ? parseFloat(location.installedCapacity) : null;
  const utilRows   = Array.isArray(location?.utilization) ? location.utilization : [];
  const latestUtil = utilRows.length > 0 ? utilRows[utilRows.length - 1] : null;
  const avgDaily   = latestUtil ? parseFloat(latestUtil.avgDailyUniqueBadges ?? 0) : null;

  // Aggregate leaseAnnual across active leases using annualRentUsd
  // (resolveCurrentYearRentUsd value — per memory: always use this field).
  const leaseAnnualSum = activeLeases.reduce((sum, l) => {
    const rent = l.contractualAnnualRentUsd !== undefined ? l.contractualAnnualRentUsd : l.annualRentUsd;
    if (rent == null) return sum;
    return sum + Number(rent);
  }, 0);
  const leaseAnnual = leaseAnnualSum > 0 ? leaseAnnualSum : null;

  // Departments from opex, scoped to latest fiscal year.
  const allDepts = safeOpex?.departments ?? [];
  const latestFY = allDepts.reduce((mx, d) => Math.max(mx, d.fiscalYear ?? 0), 0);
  const departments = latestFY > 0
    ? allDepts.filter((d) => d.fiscalYear === latestFY)
    : allDepts;
  const hasOpexData = departments.length > 0;

  let totalOccupancyYr = null;
  let costPerSf        = null;
  let costPerSeat      = null;

  if (hasOpexData && leaseAnnual != null) {
    const m = computeOpexMetrics({
      departments,
      seatCount,
      avgDaily: avgDaily && avgDaily > 0 ? avgDaily : null,
      leaseAnnual,
      sqft: latestFY === rateYear ? annualSf : null,
    });
    totalOccupancyYr = m.fullyLoadedTotal;
    costPerSf        = m.fullyLoadedPerSqft ?? m.basePerSqft;
    costPerSeat      = m.fullyLoadedPerSeat ?? m.basePerSeat;
  } else if (leaseAnnual != null) {
    // Lease-only fallback when opex data is absent.
    totalOccupancyYr = leaseAnnual;
    costPerSf        = annualSf > 0 ? leaseAnnual / annualSf : null;
    costPerSeat      = seatCount  ? leaseAnnual / seatCount  : null;
  }

  // ── Pane title labels ──────────────────────────────────────────────────────
  const leaseCountTag    = leaseCount === 1 ? "1 lease"
                         : leaseCount  > 1  ? `${leaseCount} leases`
                         : null;

  // ── Key dates ─────────────────────────────────────────────────────────────
  // Only use leases that are confirmed to belong to the current location.
  // No raw activeLease fallback here — using it would reintroduce stale data
  // from the parent for the one render before the useEffect fires.
  const rawKeyDates      = deriveAllKeyDateItems(activeLeases.filter(l => !l.dateReadFailed));
  const dateAmbiguities = activeLeases.flatMap(l => selectRecordDateEvents(l).ambiguities);
  const expiryLeaseByKeyDateId = new Map(
    activeLeases.map((lease) => [`${lease.id}-kd-expiry`, lease]),
  );
  const isActionableExpiryItem = (item) => {
    if (item.label !== "Lease expiry") return false;
    const lease = expiryLeaseByKeyDateId.get(item.id);
    return Boolean(lease && isActionablePastDueExpiry(lease, {
      date: item.date,
      officeStage: location?.stage,
    }));
  };
  // Keep actionable expiry rows visible past the generic 31-day overdue
  // cutoff; otherwise the neutral Lease row would be their only date display
  // and the urgency cue would disappear entirely.
  const filteredKeyDates = rawKeyDates.filter((item) => (
    isWithinOverdueWindow(item.date) || isActionableExpiryItem(item)
  ));

  // ── Decisions ─────────────────────────────────────────────────────────────
  const normalizedDecisions = Array.isArray(projectDecisions)
    ? projectDecisions
        .filter((d) => !d.status || d.status === "open")
        .map(normalizeProjectDecision)
        .filter(Boolean)
    : [];
  const derivedDecisions  = deriveDecisionItems(activeLeaseFitsHere ? activeLease : null, location);
  const filteredDecisions = [...normalizedDecisions, ...derivedDecisions]
    .filter((d) => isWithinOverdueWindow(d.targetDate));

  // ── Unified "What's ahead" list (key dates + decisions, capped together) ──
  // Merge into a single flat list sorted by date; decisions carry _type:"decision".
  const aheadKeyDateRows = filteredKeyDates.map((item) => {
    return {
      ...item,
      _type: "keydate",
      _sortKey: item.date,
      isActionablePastDue: isActionableExpiryItem(item),
    };
  });
  const aheadDecisionRows = filteredDecisions.map((d) => ({
    id:       d.id,
    label:    d.id === "decision-lease-renewal" ? "Renew vs renegotiate" : d.title,
    subline:  d.id === "decision-lease-renewal" ? `${String(location?.address || location?.market || "").split(",")[0]} lease` : d.owner,
    date:     d.targetDate,
    _type:    "decision",
    _sortKey: d.targetDate,
  }));
  const allAheadRows = [...aheadKeyDateRows, ...aheadDecisionRows].sort((a, b) => {
    const da = parseDateStr(a._sortKey);
    const db = parseDateStr(b._sortKey);
    if (!da && !db) return (a.label ?? "").localeCompare(b.label ?? "");
    if (!da) return 1;
    if (!db) return -1;
    const dt = da.getTime() - db.getTime();
    if (dt !== 0) return dt;
    return (a.label ?? "").localeCompare(b.label ?? "");
  });

  const hasAhead = allAheadRows.length > 0;
  const aheadLoading = Boolean(locationId && allLeasesForId !== locationId && !activeLeaseFitsHere);

  // Cap the combined list at 5; "Show N more" expands all.
  const AHEAD_CAP   = 5;
  const visibleRows = showAllDates ? allAheadRows : allAheadRows.slice(0, AHEAD_CAP);
  const hiddenCount = allAheadRows.length - visibleRows.length;

  const aheadRowsList = (
    <>
            {visibleRows.map((row, idx) => (
              <AheadRow
                key={row.id}
                tag={row._type === "decision" ? "Decision" : shortDateTag(row.label)}
                label={row.label}
                subline={row.subline || row.leaseId}
                event={row}
                dateStr={row.date}
                isActionablePastDue={row.isActionablePastDue}
                isFirst={idx === 0}
              />
            ))}
            {hiddenCount > 0 && (
              <button
                data-testid="lac-show-more-dates"
                onClick={() => setShowAllDates(true)}
                style={{
                  background:  "none",
                  border:      "none",
                  cursor:      "pointer",
                  color:       T.blue,
                  fontSize:    T.fsSm,
                  fontWeight:  T.fwSemibold,
                  textAlign:   "left",
                  display:     "block",
                  borderTop:   `1px solid ${T.hair}`,
                  width:       "100%",
                  paddingTop:  6,
                  marginTop:   2,
                  paddingLeft: 0,
                }}
              >
                + {hiddenCount} more
              </button>
            )}
    </>
  );

  // Standalone row-3 card (portal mode). Same rows, cap and "+N more" as the
  // inline section; a written empty state replaces the previous silent hide
  // because the card itself is always present in row 3.
  const aheadCard = (
    <section
      data-testid="whats-ahead-card"
      aria-labelledby="whats-ahead-title"
      aria-busy={aheadLoading}
      style={{
        background:   T.card,
        border:       `1px solid ${T.border}`,
        borderRadius: 8,
        boxShadow:    T.cardShadow,
        padding:      "0 20px 14px",
        minWidth:     0,
      }}
    >
      {(dateReadErrors.length > 0 || dateAmbiguities.length > 0) && <p role="alert" style={{ color: T.mid, fontSize: T.fsXs }}>
        {dateReadErrors.length > 0 ? "Some lease dates couldn't be loaded. Reopen this record to retry." : "Some date sources need review; unverified dates have not been promoted to recorded facts."}
      </p>}
      <div style={{ display: "flex", alignItems: "center", gap: 9, minHeight: 40 }}>
        <div aria-hidden="true" style={{ width: 3, height: 16, borderRadius: 2, background: T.blue, flexShrink: 0 }} />
        <h3 id="whats-ahead-title" style={{ margin: 0, fontSize: T.fsBase, fontWeight: T.fwBold, color: T.dark }}>
          What&apos;s ahead
        </h3>
      </div>
      {aheadLoading ? (
        <div role="status" style={{ color: T.light, fontSize: T.fsSm5 }}>Loading upcoming dates…</div>
      ) : hasAhead ? (
        <div data-testid="lac-ahead-section">{aheadRowsList}</div>
      ) : (
        <div data-testid="whats-ahead-empty" style={{ color: T.light, fontSize: T.fsSm5, fontStyle: "italic", padding: "6px 0 2px" }}>
          {dateReadErrors.length > 0 ? "Upcoming lease dates unavailable." : "No upcoming lease dates or open decisions for this office."}
        </div>
      )}
    </section>
  );

  const portalMode = aheadPortalTarget !== undefined;

  return (
    <div
      data-testid="lease-and-cost-pane"
      style={{
        borderRadius:  10,
        padding:       "0 20px 16px",
        display:       "flex",
        flexDirection: "column",
        boxSizing:     "border-box",
      }}
    >
      {/* ── Header ── */}
      <div
        style={{
          display:    "flex",
          alignItems: "center",
          gap:        9,
          margin:     "0 -20px",
          padding:    "8px 20px",
          minHeight:  40,
        }}
      >
        <div
          style={{
            width:        3,
            height:       16,
            borderRadius: 2,
            background:   T.blue,
            flexShrink:   0,
          }}
        />
        <h3
          style={{
            margin:     0,
            fontSize:   T.fsBase,
            fontWeight: T.fwBold,
            color:      T.dark,
          }}
        >
          Lease &amp; cost
        </h3>
        {leaseCountTag && (
          <span
            style={{
              fontSize:      T.fsXxxs5,
              fontWeight:    T.fwBold,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color:         T.blue,
              background:    T.blueSoft,
              border:        `1px solid ${T.blueSoftBorder}`,
              borderRadius:  5,
              padding:       "2px 7px",
            }}
          >
            {leaseCountTag}
          </span>
        )}
      </div>

      <div data-testid="lac-body">
        {/* Lease header row(s) */}
        {activeLeases.map((lease) => (
          <LeaseRow key={lease.id} lease={lease} locationStage={location?.stage} onOpenLease={onOpenLease} onEditAreas={onEditAreas} />
        ))}

        {/* Cost rollup tiles */}
        <div
          data-testid="lac-rollup"
          style={{
            display:             "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap:                 8,
            margin:              activeLeases.length ? "4px 0 12px" : "0 0 12px",
          }}
        >
          <KpiTile
            label="Occupancy / yr"
            value={fmtDollarsHero(totalOccupancyYr)}
            testId="lac-kpi-total"
          />
          <KpiTile
            label="Cost / sf"
            value={fmtDollarsHero(costPerSf)}
            testId="lac-kpi-sf"
          />
          <KpiTile
            label="Cost / seat"
            value={fmtDollarsHero(costPerSeat)}
            testId="lac-kpi-seat"
          />
        </div>

        <LeaseAreasStrip
          key={locationId || "no-location"}
          lease={officeAreaSummaryLease}
          location={location}
          officeAreas={officeAreas}
          officeLeaseCount={officeAreaLeases.length}
          collapsible
          wrapperStyle={{ margin: 0 }}
        />

        {!portalMode && hasAhead && (
          <div
            data-testid="lac-ahead-section"
            style={{
              borderTop:  `1px solid ${T.hair}`,
              marginTop:  11,
              paddingTop: 11,
            }}
          >
            <div
              style={{
                fontSize:      T.fsXxs,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                color:         T.light,
                fontWeight:    T.fwBold,
                marginBottom:  6,
              }}
            >
              What&apos;s ahead
            </div>
            {aheadRowsList}
          </div>
        )}
      </div>
      {portalMode && aheadPortalTarget && createPortal(aheadCard, aheadPortalTarget)}
    </div>
  );
}
